import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { IressButton, IressIcon } from '@iress-oss/ids-components';
import type { CreditState } from '@/lib/wallet';
import { MAX_MESSAGE_LENGTH } from '@/lib/constants';
import { CreditBanner } from './CreditBanner';
import './ChatInput.css';

const MAX_CHARS = MAX_MESSAGE_LENGTH;
const MAX_HEIGHT_PX = 140;

/** Imperative handle the parent uses to focus the input and prefill its text. */
export interface ChatInputHandle {
  focus: () => void;
  setText: (text: string) => void;
}

interface ChatInputProps {
  onSubmit: (message: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * When this changes to a non-null value, the textarea is (re-)populated with
   * it — used by the disambiguation flow to re-populate the ORIGINAL message
   * unchanged after a candidate is selected (Model X). The wrapper bumps a
   * paired nonce so re-selecting the same text still refills.
   */
  injectedValue?: string | null;
  /** Nonce paired with injectedValue; a change triggers re-population. */
  injectedValueNonce?: number;
  /**
   * Current credit state from useWallet. When 'low', the input border turns
   * amber; when 'exhausted', it turns red. All other states use the default
   * border colour.
   */
  creditState?: CreditState;
  /**
   * Remaining credit percentage — passed to CreditBanner shown on focus.
   * Only relevant when creditState is 'low'.
   */
  remainingPercentage?: number | null;
  /**
   * Called when the "View usage" link in the CreditBanner is clicked.
   */
  onViewUsage?: () => void;
}

/** Returns the CSS border-modifier class for the current credit state. */
function getCreditBorderClass(creditState: CreditState | undefined): string {
  if (creditState === 'low') return ' porter-input--low';
  if (creditState === 'exhausted') return ' porter-input--exhausted';
  return '';
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      onSubmit,
      loading = false,
      disabled = false,
      placeholder = 'Ask about a client, task or meetings',
      injectedValue = null,
      injectedValueNonce = 0,
      creditState,
      remainingPercentage = null,
      onViewUsage,
    },
    ref,
  ) => {
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Expose focus() and setText() to the parent. setText prefills the input
    // (e.g. the "Change question" control copies the original query back in)
    // and places the caret at the end so the adviser can edit immediately.
    useImperativeHandle(
      ref,
      () => ({
        focus: () => textareaRef.current?.focus(),
        setText: (text: string) => {
          setValue(text.slice(0, MAX_CHARS));
          requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (!el) return;
            el.focus();
            const end = el.value.length;
            el.setSelectionRange(end, end);
          });
        },
      }),
      [],
    );

    // Re-populate the input when an injected value arrives (Model X: the
    // original message is restored unchanged for the adviser to re-send).
    useEffect(() => {
      if (injectedValue != null) {
        setValue(injectedValue.slice(0, MAX_CHARS));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [injectedValueNonce]);

    // Capture the textarea DOM node
    const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
      textareaRef.current = el;
    }, []);

    // Auto-grow textarea
    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT_PX)}px`;
    }, [value]);

    const handleSubmit = useCallback(() => {
      const trimmed = value.trim();
      if (!trimmed || disabled || loading) return;
      onSubmit(trimmed);
      setValue('');
    }, [value, disabled, loading, onSubmit]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      },
      [handleSubmit],
    );

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        if (newValue.length <= MAX_CHARS) {
          setValue(newValue);
        }
      },
      [],
    );

    const canSubmit = value.trim().length > 0 && !disabled && !loading;

    const borderClass = getCreditBorderClass(creditState);

    return (
      <>
        {/* CreditBanner is always rendered when in a warning state so the
            footer height stays constant — no layout shift on focus/blur.
            visibility:hidden reserves the space when not focused. */}
        {onViewUsage && (
          <CreditBanner
            creditState={creditState ?? 'healthy'}
            remainingPercentage={remainingPercentage}
            onViewUsage={onViewUsage}
            hidden={!isFocused}
          />
        )}
        <div className={`porter-input${borderClass}`}>
          <textarea
            ref={setRefs}
            className="porter-input__textarea"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              // Keep the banner visible when focus moves from the textarea to
              // the "View usage" button inside CreditBanner. Without this check
              // the banner would be hidden (visibility:hidden) before the button
              // can receive the click, making the CTA unreachable by pointer or
              // keyboard.
              const relatedTarget = e.relatedTarget as HTMLElement | null;
              const bannerLink = relatedTarget?.closest('.porter-credit-banner');
              if (!bannerLink) setIsFocused(false);
            }}
            placeholder={placeholder}
            disabled={disabled || loading}
            rows={1}
            aria-label="Chat message"
          />
          <IressButton
            mode="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="Send message"
            className="porter-input__send"
          >
            <IressIcon name="arrow_upward" />
          </IressButton>
        </div>
      </>
    );
  },
);

ChatInput.displayName = 'ChatInput';
